import { actions, actionTypes } from "../actions";
import {
  Currency,
  DateTimeZone,
  ITripTerminus,
  IUserPreferences,
  Locale,
  TripCategory,
  IDateBucket,
  IMonthBucket,
  ITimeRange,
  FareclassShelfBrandName,
  SliceStopCountFilter,
  AirlineCode,
  IPassengerCounts,
  UserFlightPreferencesPayload,
  CallState,
} from "redmond";
import { ITripTerminusCategory } from "../types";
import {
  getMulticityDepartureStringValuesFromParsedShopQuery,
  IFlightShopParsedQuery,
  IMulticityFlightShopParsedQuery,
  parseQueryFromString,
} from "../../shop/utils/parseQueryString";
import {
  getMulticityRoutesWithNewDepartureDate,
  getMulticityRoutesWithNewDestination,
  getMulticityRoutesWithNewOrigin,
  getMulticityRoutesWithRemovedIndex,
} from "../../shop/utils/getUpdatedMultiCityRoutes";
import dayjs from "dayjs";

export interface MulticityRoute {
  origin: ITripTerminus | null;
  destination: ITripTerminus | null;
  departureDate: Date | null;
}

export enum MobileFlightSearchStep {
  LocationSearch,
  CalendarPicker,
  Complete,
}

export interface IFlightNumberFilter {
  flightNumber: string;
  airlineCode: string;
}
export interface IFilterState {
  stopsOption: SliceStopCountFilter;
  maxPriceFilter: number;
  fareclassOptionFilter: FareclassOptionFilter;
  outboundDepartureTimeRange: ITimeRange;
  outboundArrivalTimeRange: ITimeRange;
  returnDepartureTimeRange: ITimeRange;
  returnArrivalTimeRange: ITimeRange;
  airlineFilter: AirlineCode[];
  airportFilter: string[];
  flightNumberFilter: IFlightNumberFilter[];
  baggageTransfersFilter: boolean;
  durationFilter: number;
  policyFilter: boolean;
  userFlightPreferences?: UserFlightPreferencesPayload;
  userFlightPreferencesCallState?: CallState;
  updateUserFlightPreferencesCallState?: CallState;
  applyUserFlightPreferences: boolean;
  userPreferencesNotAvailable: boolean;
}
export interface IFlightSearchState extends IFilterState {
  openDatesModal: boolean;
  numTravelers: number;
  tripCategory: TripCategory;
  origin: ITripTerminus | null;
  originCategories: ITripTerminusCategory[] | null;
  originCategoriesLoading: boolean | null;
  destination: ITripTerminus | null;
  destinationCategories: ITripTerminusCategory[] | null;
  destinationCategoriesLoading: boolean | null;
  currency: string | null;
  calendarQueryOrigin: ITripTerminus | null;
  calendarQueryDestination: ITripTerminus | null;
  departureMonths: IMonthBucket[] | null;
  departureDateBuckets: IDateBucket[] | null;
  departureDateBucketsLoading: boolean | null;
  departureDate: Date | null;
  returnDate: Date | null;
  multiCityRoutes: MulticityRoute[];
  preferences: IUserPreferences;
  adultsCount: number;
  childrenCount: number;
  infantsInSeatCount: number;
  infantsOnLapCount: number;
  awaitingRefetch: boolean;
  mobileSearchStep: MobileFlightSearchStep;
  hasOriginAutocompleteError: boolean;
  hasDestinationAutocompleteError: boolean;
}

export type FareclassOptionFilter = {
  [key in FareclassShelfBrandName]: boolean;
};

export const initialFareclassOptionFilter: FareclassOptionFilter = {
  basic: false,
  standard: false,
  enhanced: false,
  premium: false,
  luxury: false,
};

export const noLCCFareclassOptionFilter: FareclassOptionFilter = {
  basic: false,
  standard: true,
  enhanced: true,
  premium: true,
  luxury: true,
};

export const INITIAL_MAX_PRICE = 0;
// number of minutes in a day = 60 min/hour * 24 hour = 1440 min
export const TIME_RANGE_MAX = 1439;

export const MAXIMUM_COUNT = 6;
export const MINIMUM_ADULT_COUNT = 1;

export const initialTimeRange: ITimeRange = {
  min: 0,
  // note: time measured in minutes
  max: TIME_RANGE_MAX,
};

export const initialFilterOptions: IFilterState = {
  stopsOption: SliceStopCountFilter.ANY_NUMBER,
  maxPriceFilter: 0,
  fareclassOptionFilter: initialFareclassOptionFilter,
  outboundDepartureTimeRange: initialTimeRange,
  outboundArrivalTimeRange: initialTimeRange,
  returnDepartureTimeRange: initialTimeRange,
  returnArrivalTimeRange: initialTimeRange,
  airlineFilter: [],
  airportFilter: [],
  flightNumberFilter: [],
  baggageTransfersFilter: true,
  durationFilter: 0,
  policyFilter: false,
  userFlightPreferences: {
    fareClasses: {
      basic: false,
      standard: false,
      enhanced: false,
      premium: false,
      luxury: false,
    },
    airlines: [],
    nonStopOnly: false,
  },
  userFlightPreferencesCallState: CallState.NotCalled,
  updateUserFlightPreferencesCallState: CallState.NotCalled,
  applyUserFlightPreferences: false,
  userPreferencesNotAvailable: false,
};

export const initialState = (query?: string): IFlightSearchState => {
  const defaults = {
    numTravelers: 1,
    tripCategory: TripCategory.ROUND_TRIP,
    originCategories: [],
    destinationCategories: [],
    originCategoriesLoading: false,
    destinationCategoriesLoading: false,
    openDatesModal: false,

    origin: null,
    destination: null,
    currency: null,
    calendarQueryOrigin: null,
    calendarQueryDestination: null,
    departureMonths: null,
    departureDateBuckets: null,
    departureDateBucketsLoading: false,
    departureDate: null,
    returnDate: null,
    multiCityRoutes: [
      {
        origin: null,
        destination: null,
        departureDate: null,
      },
      {
        origin: null,
        destination: null,
        departureDate: null,
      },
    ],
    preferences: {
      locale: Locale.EN_US,
      currency: Currency.CAD,
      timeZone: DateTimeZone.AMERICA_TORONTO,
    },
    adultsCount: MINIMUM_ADULT_COUNT,
    childrenCount: 0,
    infantsInSeatCount: 0,
    infantsOnLapCount: 0,
    awaitingRefetch: false,
    mobileSearchStep: MobileFlightSearchStep.LocationSearch,
    hasOriginAutocompleteError: false,
    hasDestinationAutocompleteError: false,
    ...initialFilterOptions,
  };

  if (query) {
    const queryParams = parseQueryFromString(query);
    const tripCategory = queryParams.tripCategory;
    const isMulticity = tripCategory === TripCategory.MULTI_CITY;

    const getDepartureValues = () => {
      if (isMulticity) {
        const multicityQueryParams = parseQueryFromString(
          query
        ) as IMulticityFlightShopParsedQuery;
        const multiCityRoutes =
          getMulticityDepartureStringValuesFromParsedShopQuery(
            multicityQueryParams
          ).map((routeStrings) => {
            return {
              origin: null,
              destination: null,
              departureDate: dayjs(routeStrings.departureDate).toDate(),
            };
          });
        return { multiCityRoutes, tripCategory: TripCategory.MULTI_CITY };
      } else {
        const defaultQueryParams = parseQueryFromString(
          query
        ) as IFlightShopParsedQuery;
        return {
          departureDate:
            defaultQueryParams.departureDate || defaults.departureDate,
          returnDate: defaultQueryParams.returnDate || defaults.returnDate,
        };
      }
    };

    return {
      ...defaults,
      tripCategory: queryParams.tripCategory || defaults.tripCategory,
      ...getDepartureValues(),
    };
  } else {
    return defaults;
  }
};

export function reducer(
  state: IFlightSearchState = initialState(window?.location?.search),
  action: actions.FlightSearchActions
): IFlightSearchState {
  switch (action.type) {
    case actionTypes.SET_OPEN_DATES_MODAL:
      return {
        ...state,
        openDatesModal: action.openDatesModal,
      };

    case actionTypes.SET_TRIP_CATEGORY:
      const { tripCategory, updateTripCategoryOnly } = action;
      return {
        ...state,
        tripCategory,
        ...(updateTripCategoryOnly ? undefined : { returnDate: null }),
      };

    case actionTypes.SET_PASSENGER_COUNTS:
      const counts = action.counts as IPassengerCounts;
      const numTravelers =
        counts.adultsCount +
        counts.childrenCount +
        counts.infantsInSeatCount +
        counts.infantsOnLapCount;
      return { ...state, ...action.counts, numTravelers };

    case actionTypes.SET_ORIGIN:
      const { origin } = action;
      return { ...state, origin, awaitingRefetch: true };

    case actionTypes.SET_ORIGIN_CATEGORIES:
      return {
        ...state,
        originCategories: action.originCategories,
        originCategoriesLoading: false,
        hasOriginAutocompleteError: false,
      };

    case actionTypes.FETCH_ORIGIN_CATEGORIES:
      return { ...state, originCategories: [], originCategoriesLoading: true };

    case actionTypes.SET_DESTINATION:
      const { destination } = action;
      return { ...state, destination, awaitingRefetch: true };

    case actionTypes.SET_DESTINATION_CATEGORIES:
      return {
        ...state,
        destinationCategories: action.destinationCategories,
        destinationCategoriesLoading: false,
        hasDestinationAutocompleteError: false,
      };

    case actionTypes.FETCH_DESTINATION_CATEGORIES:
      return {
        ...state,
        destinationCategories: [],
        destinationCategoriesLoading: true,
      };

    case actionTypes.FETCH_DEPARTURE_CALENDAR:
      return {
        ...state,
        currency: null,
        departureMonths: null,
        departureDateBuckets: null,
        departureDateBucketsLoading: true,
        calendarQueryOrigin: action.origin,
        calendarQueryDestination: action.destination,
      };

    case actionTypes.SET_DEPARTURE_CALENDAR:
      return {
        ...state,
        currency:
          typeof action.currency === "undefined" ? null : action.currency,
        departureMonths:
          typeof action.departureMonths === "undefined"
            ? null
            : action.departureMonths,
        departureDateBuckets:
          typeof action.departureDateBuckets === "undefined"
            ? null
            : action.departureDateBuckets,
        departureDateBucketsLoading: false,
      };

    case actionTypes.SET_DEPARTURE_DATE:
      return {
        ...state,
        departureDate: action.date ? action.date : null,
        awaitingRefetch: true,
      };

    case actionTypes.SET_RETURN_DATE:
      return {
        ...state,
        returnDate: action.date ? action.date : null,
        awaitingRefetch: true,
      };

    case actionTypes.SET_MULTICITY_ORIGIN:
      return {
        ...state,
        multiCityRoutes: getMulticityRoutesWithNewOrigin(
          state.multiCityRoutes,
          action.origin,
          action.index
        ),
        // awaitingRefetch: true,
      };

    case actionTypes.SET_MULTICITY_DESTINATION:
      return {
        ...state,
        multiCityRoutes: getMulticityRoutesWithNewDestination(
          state.multiCityRoutes,
          action.destination,
          action.index
        ),
        // awaitingRefetch: true,
      };

    case actionTypes.SET_MULTICITY_DEPARTURE_DATE:
      return {
        ...state,
        multiCityRoutes: getMulticityRoutesWithNewDepartureDate(
          state.multiCityRoutes,
          action.departureDate,
          action.index
        ),
        // awaitingRefetch: true,
      };

    case actionTypes.SET_ALL_MULTICITY_ROUTES:
      return {
        ...state,
        multiCityRoutes: action.multicityRoutes,
      };

    case actionTypes.ADD_MULTICITY_ROUTE:
      const lastSelectedDestination =
        state.multiCityRoutes[state.multiCityRoutes.length - 1].destination;
      return {
        ...state,
        multiCityRoutes: state.multiCityRoutes.concat({
          origin: lastSelectedDestination,
          destination: null,
          departureDate: null,
        }),
      };

    case actionTypes.REMOVE_MULTICITY_ROUTE:
      return {
        ...state,
        multiCityRoutes: getMulticityRoutesWithRemovedIndex(
          state.multiCityRoutes,
          action.index
        ),
      };

    case actionTypes.SET_STOPS_OPTION:
      return { ...state, stopsOption: action.stopsOption };

    case actionTypes.SET_MAX_PRICE_FILTER:
      return { ...state, maxPriceFilter: action.maxPriceFilter };

    case actionTypes.SET_FARECLASS_OPTION_FILTER:
      return {
        ...state,
        fareclassOptionFilter: { ...action.fareclassOptionFilter },
      };

    case actionTypes.SET_BAGGAGE_TRANSFERS_FILTER:
      return {
        ...state,
        baggageTransfersFilter: action.value,
      };

    case actionTypes.SET_OUTBOUND_DEPARTURE_TIME_RANGE_FILTER:
      return {
        ...state,
        outboundDepartureTimeRange: action.outboundDepartureTimeRange,
      };

    case actionTypes.SET_OUTBOUND_ARRIVAL_TIME_RANGE_FILTER:
      return {
        ...state,
        outboundArrivalTimeRange: action.outboundArrivalTimeRange,
      };

    case actionTypes.SET_RETURN_DEPARTURE_TIME_RANGE_FILTER:
      return {
        ...state,
        returnDepartureTimeRange: action.returnDepartureTimeRange,
      };

    case actionTypes.SET_RETURN_ARRIVAL_TIME_RANGE_FILTER:
      return {
        ...state,
        returnArrivalTimeRange: action.returnArrivalTimeRange,
      };

    case actionTypes.SET_AIRLINE_FILTER:
      return {
        ...state,
        airlineFilter: [...action.airlineFilter],
      };

    case actionTypes.SET_AIRPORT_FILTER:
      return {
        ...state,
        airportFilter: [...action.airportFilter],
      };

    case actionTypes.SET_POLICY_FILTER:
      return {
        ...state,
        policyFilter: action.policyFilter,
      };

    case actionTypes.SET_DURATION_FILTER:
      return {
        ...state,
        durationFilter: action.duration,
      };

    case actionTypes.SET_AWAITING_REFETCH:
      return {
        ...state,
        awaitingRefetch: action.awaitingRefetch,
      };

    case actionTypes.SET_MOBILE_SEARCH_PROGRESS:
      return {
        ...state,
        mobileSearchStep: action.step,
      };

    case actionTypes.SET_FLIGHT_NUMBER_FILTER:
      const airlines = action.flightNumberFilter.map(
        (filter) => filter.airlineCode
      );

      // Reset airline filter if chosen flight number is on a different airline.
      if (
        !!airlines.length &&
        !state.airlineFilter.some((airline) => airlines.includes(airline))
      ) {
        return {
          ...state,
          airlineFilter: [],
          flightNumberFilter: [...action.flightNumberFilter],
        };
      }

      return {
        ...state,
        flightNumberFilter: [...action.flightNumberFilter],
      };
    case actionTypes.SET_ORIGIN_AUTOCOMPLETE_ERROR: {
      return {
        ...state,
        hasOriginAutocompleteError: action.error,
        originCategoriesLoading: false,
      };
    }
    case actionTypes.SET_DESTINATION_AUTOCOMPLETE_ERROR: {
      return {
        ...state,
        hasDestinationAutocompleteError: action.error,
        destinationCategoriesLoading: false,
      };
    }
    case actionTypes.FETCH_USER_FLIGHT_PREFERENCES:
      return {
        ...state,
        userFlightPreferencesCallState: CallState.InProcess,
      };
    case actionTypes.SET_USER_FLIGHT_PREFERENCES:
      return {
        ...state,
        userFlightPreferences: action.flightPreferences,
        userFlightPreferencesCallState: CallState.Success,
      };
    case actionTypes.SET_USER_FLIGHT_PREFERENCES_CALL_STATE_FAILED:
      return {
        ...state,
        userFlightPreferencesCallState: CallState.Failed,
      };
    case actionTypes.SET_APPLY_USER_FLIGHT_PREFERENCES:
      return {
        ...state,
        applyUserFlightPreferences: action.applyPreferences,
      };
    case actionTypes.SET_USER_PREFERENCES_NOT_AVAILABLE:
      return {
        ...state,
        userPreferencesNotAvailable: action.userPreferencesNotAvailable,
      };
    default:
      return state;
  }
}

export * from "./selectors";
